import { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import api from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ShareModal from '../components/ShareModal';

interface BookingLink {
  id: string;
  token: string;
  opponentName: string;
  opponentEmail: string | null;
  leagueName: string | null;
  legs: number;
  expiresAt: string;
  isUsed: boolean;
  isExpired: boolean;
  createdAt: string;
  url: string;
  gameType?: string;
  doubleIn?: boolean;
  doubleOut?: boolean;
}

interface Format {
  id: string;
  name: string;
  gameType: string;
  defaultLegs: number;
  doubleIn: boolean;
  doubleOut: boolean;
}

interface League {
  id: string;
  name: string;
  abbreviation: string | null;
  formats: Format[];
}

export default function BookingLinks() {
  const [links, setLinks] = useState<BookingLink[]>([]);
  const [activeCount, setActiveCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [leagues, setLeagues] = useState<League[]>([]);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [opponentName, setOpponentName] = useState('');
  const [opponentEmail, setOpponentEmail] = useState('');
  const [selectedLeagueId, setSelectedLeagueId] = useState('');
  const [selectedFormatId, setSelectedFormatId] = useState('');
  const [leagueName, setLeagueName] = useState('');
  const [legs, setLegs] = useState(5);
  const [gameType, setGameType] = useState('501');
  const [doubleIn, setDoubleIn] = useState(false);
  const [doubleOut, setDoubleOut] = useState(true);
  const [expiryHours, setExpiryHours] = useState(24);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Copy state
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Share modal state
  const [shareLink, setShareLink] = useState<BookingLink | null>(null);

  const fetchLinks = async () => {
    try {
      const response = await api.get('/booking-links');
      setLinks(response.data.links);
      setActiveCount(response.data.activeCount);
    } catch {
      setError('Failed to load booking links');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLeagues = async () => {
    try {
      const response = await api.get('/leagues');
      setLeagues(response.data.leagues);
    } catch {
      // Leagues are optional
    }
  };

  useEffect(() => {
    fetchLinks();
    fetchLeagues();
  }, []);

  // Get formats for selected league
  const selectedLeague = leagues.find(l => l.id === selectedLeagueId);
  const availableFormats = selectedLeague?.formats || [];

  // Handle format selection - auto-populate settings
  const handleFormatChange = (formatId: string) => {
    setSelectedFormatId(formatId);
    const format = availableFormats.find(f => f.id === formatId);
    if (format) {
      setLegs(format.defaultLegs);
      setGameType(format.gameType);
      setDoubleIn(format.doubleIn);
      setDoubleOut(format.doubleOut);
    }
  };

  // Handle league selection
  const handleLeagueChange = (leagueId: string) => {
    setSelectedLeagueId(leagueId);
    setSelectedFormatId('');
    const league = leagues.find(l => l.id === leagueId);
    setLeagueName(league?.name || '');
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await api.post('/booking-links', {
        opponentName,
        opponentEmail: opponentEmail || undefined,
        leagueName: leagueName || undefined,
        leagueFormatId: selectedFormatId || undefined,
        legs,
        gameType,
        doubleIn,
        doubleOut,
        expiryHours
      });
      await fetchLinks();
      setShowForm(false);
      setOpponentName('');
      setOpponentEmail('');
      setSelectedLeagueId('');
      setSelectedFormatId('');
      setLeagueName('');
      setLegs(5);
      setGameType('501');
      setDoubleIn(false);
      setDoubleOut(true);
      setExpiryHours(24);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to create link');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this booking link?')) return;

    try {
      await api.delete(`/booking-links/${id}`);
      await fetchLinks();
    } catch {
      setError('Failed to delete link');
    }
  };

  const copyToClipboard = async (url: string, id: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      setError('Failed to copy link');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title mb-0">Booking Links</h1>
          <p className="text-gray-400 text-sm">{activeCount} active link(s)</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary">
          Create Link
        </button>
      </div>

      {error && (
        <div className="bg-red-900/50 text-red-300 px-4 py-3 rounded-lg text-sm border border-red-800">
          {error}
          <button onClick={() => setError('')} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Create Form */}
      {showForm && (
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Create Booking Link</h2>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Opponent Name *</label>
                <input
                  type="text"
                  value={opponentName}
                  onChange={(e) => setOpponentName(e.target.value)}
                  className="input"
                  required
                  maxLength={100}
                />
              </div>
              <div>
                <label className="label">Opponent Email (optional)</label>
                <input
                  type="email"
                  value={opponentEmail}
                  onChange={(e) => setOpponentEmail(e.target.value)}
                  className="input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">League</label>
                <select
                  value={selectedLeagueId}
                  onChange={(e) => handleLeagueChange(e.target.value)}
                  className="input"
                >
                  <option value="">-- Casual (no league) --</option>
                  {leagues.map((league) => (
                    <option key={league.id} value={league.id}>
                      {league.name} {league.abbreviation ? `(${league.abbreviation})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Format</label>
                <select
                  value={selectedFormatId}
                  onChange={(e) => handleFormatChange(e.target.value)}
                  className={`input ${!selectedLeagueId ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={!selectedLeagueId}
                >
                  <option value="">-- Select format --</option>
                  {availableFormats.map((format) => (
                    <option key={format.id} value={format.id}>
                      {format.name} ({format.gameType}, {format.defaultLegs} legs)
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="label">Game Type {selectedFormatId && <span className="text-gray-500 text-xs">(set by format)</span>}</label>
                <select
                  value={gameType}
                  onChange={(e) => setGameType(e.target.value)}
                  className={`input ${selectedFormatId ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={!!selectedFormatId}
                >
                  <option value="501">501</option>
                  <option value="301">301</option>
                  <option value="701">701</option>
                  <option value="Cricket">Cricket</option>
                </select>
              </div>
              <div>
                <label className="label">Legs</label>
                <select
                  value={legs}
                  onChange={(e) => setLegs(parseInt(e.target.value))}
                  className="input"
                >
                  {[...Array(21)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col justify-end pb-2">
                <label className={`flex items-center gap-2 text-gray-300 ${selectedFormatId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    checked={doubleIn}
                    onChange={(e) => setDoubleIn(e.target.checked)}
                    className="rounded bg-[#1a1a3a] border-[#2a2a4a] w-5 h-5"
                    disabled={!!selectedFormatId}
                  />
                  <span className="text-sm">Double In</span>
                </label>
              </div>
              <div className="flex flex-col justify-end pb-2">
                <label className={`flex items-center gap-2 text-gray-300 ${selectedFormatId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    checked={doubleOut}
                    onChange={(e) => setDoubleOut(e.target.checked)}
                    className="rounded bg-[#1a1a3a] border-[#2a2a4a] w-5 h-5"
                    disabled={!!selectedFormatId}
                  />
                  <span className="text-sm">Double Out</span>
                </label>
              </div>
            </div>

            <div>
              <label className="label">Link Expires In</label>
              <select
                value={expiryHours}
                onChange={(e) => setExpiryHours(parseInt(e.target.value))}
                className="input w-auto"
              >
                <option value={1}>1 hour</option>
                <option value={6}>6 hours</option>
                <option value={12}>12 hours</option>
                <option value={24}>24 hours</option>
                <option value={48}>48 hours</option>
                <option value={168}>1 week</option>
              </select>
            </div>

            <div className="flex gap-2">
              <button type="submit" disabled={isSubmitting} className="btn-primary">
                {isSubmitting ? 'Creating...' : 'Create Link'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Links List */}
      {links.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-400">No booking links yet. Create one to share with opponents.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {links.map((link) => (
            <div
              key={link.id}
              className={`card ${link.isUsed || link.isExpired ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-white">{link.opponentName}</h3>
                    {link.isUsed && (
                      <span className="px-2 py-0.5 bg-green-900/30 text-green-300 border border-green-800/50 rounded-full text-xs">
                        Used
                      </span>
                    )}
                    {link.isExpired && !link.isUsed && (
                      <span className="px-2 py-0.5 bg-gray-800/30 text-gray-400 border border-gray-700/50 rounded-full text-xs">
                        Expired
                      </span>
                    )}
                    {!link.isUsed && !link.isExpired && (
                      <span className="px-2 py-0.5 bg-blue-900/30 text-blue-300 border border-blue-800/50 rounded-full text-xs">
                        Active
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-400 mt-1">
                    {link.leagueName || 'Casual'} - {link.gameType || '501'} - {link.legs} legs
                    {link.doubleIn && ' - DI'}
                    {link.doubleOut && ' - DO'}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Expires: {format(parseISO(link.expiresAt), 'MMM d, yyyy h:mm a')}
                  </p>
                  <div className="mt-2 p-2 bg-[#252550] rounded text-sm font-mono text-gray-400 truncate">
                    {link.url}
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  {!link.isUsed && !link.isExpired && (
                    <>
                      <button
                        onClick={() => copyToClipboard(link.url, link.id)}
                        className="btn-primary text-sm"
                      >
                        {copiedId === link.id ? 'Copied!' : 'Copy'}
                      </button>
                      <button
                        onClick={() => setShareLink(link)}
                        className="btn-secondary text-sm"
                      >
                        Share
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => handleDelete(link.id)}
                    className="btn-secondary text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Share Modal */}
      {shareLink && (
        <ShareModal
          url={shareLink.url}
          title={shareLink.opponentName}
          onClose={() => setShareLink(null)}
        />
      )}
    </div>
  );
}
